import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { shelfZonesOnly } from "../utils/shelfZones";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  BouquetTemplate,
  ConsumablePreset,
  CreateBouquetCompletePayload,
  DefaultUom,
  PaperRollRow,
} from "../api/types";
import { ItemPicker } from "../components/ItemPicker";
import { BouquetTemplateThumb } from "../components/BouquetTemplateThumb";
import type { DashboardOutletContext } from "./dashboardOutletContext";
import {
  NO_FIGURE,
  afterCostLabel,
  bouquetCostText,
  unknownComponentsNote,
} from "../utils/unknownCost";

const euro = (n: number) => `€${n.toFixed(2)}`;

export type BouquetBuildPageProps = {
  embedOnHome?: boolean;
};

const UOMS: DefaultUom[] = ["UNIT", "STEM", "CM", "ROLL"];

type Line = { itemId: string; quantity: string; uom: DefaultUom };

type PaperLengthChoice = ConsumablePreset | "CUSTOM";

/** Acceptable color ids for a template's paper/ribbon requirement lines. */
const colorsForKind = (
  tpl: BouquetTemplate,
  kind: "PAPER_REQUIREMENT" | "RIBBON_REQUIREMENT",
): string[] =>
  tpl.lines
    .filter((l) => l.kind === kind)
    .flatMap((l) => l.acceptableColors?.map((ac) => ac.color.id) ?? []);

export function BouquetBuildPage({ embedOnHome = false }: BouquetBuildPageProps) {
  const { locations } = useOutletContext<DashboardOutletContext>();
  const [searchParams] = useSearchParams();
  const templateIdParam = searchParams.get("templateId") || "";

  const [templates, setTemplates] = useState<BouquetTemplate[]>([]);
  const [locationId, setLocationId] = useState(locations[0]?.id ?? "");
  const [zoneId, setZoneId] = useState("");
  const [templateId, setTemplateId] = useState(templateIdParam);
  const [lines, setLines] = useState<Line[]>([{ itemId: "", quantity: "1", uom: "STEM" }]);
  const [resultMsg, setResultMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [includePaper, setIncludePaper] = useState(false);
  const [paperRolls, setPaperRolls] = useState<PaperRollRow[]>([]);
  const [rollsLoading, setRollsLoading] = useState(false);
  const [selectedRollBatchId, setSelectedRollBatchId] = useState("");
  const [paperLength, setPaperLength] = useState<PaperLengthChoice>("SMALL");
  const [paperCustomCm, setPaperCustomCm] = useState("40");
  // Acceptable paper/ribbon colors from the applied template (pre-filter the roll picker).
  const [paperColorIds, setPaperColorIds] = useState<string[]>([]);
  const [ribbonColorIds, setRibbonColorIds] = useState<string[]>([]);
  const [showAllRolls, setShowAllRolls] = useState(false);

  const loc = useMemo(
    () => locations.find((l) => l.id === locationId),
    [locations, locationId],
  );

  useEffect(() => {
    if (locations.length && !locationId) {
      setLocationId(locations[0].id);
    }
  }, [locations, locationId]);

  useEffect(() => {
    if (loc?.zones.length && !zoneId) {
      const front = loc.zones.find((z) => z.zoneType === "FRONT_SHOP");
      setZoneId(front?.id ?? loc.zones[0].id);
    }
  }, [loc, zoneId]);

  const loadPaperRolls = useCallback(async () => {
    if (!locationId) return;
    setRollsLoading(true);
    try {
      const list = await api.listPaperRolls(locationId);
      setPaperRolls(list);
      setSelectedRollBatchId((prev) => {
        if (prev && list.some((r) => r.batchId === prev)) return prev;
        return list[0]?.batchId ?? "";
      });
    } catch {
      setPaperRolls([]);
      setSelectedRollBatchId("");
    } finally {
      setRollsLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    if (!includePaper || !locationId) {
      setPaperRolls([]);
      setSelectedRollBatchId("");
      return;
    }
    void loadPaperRolls();
  }, [includePaper, locationId, loadPaperRolls]);

  const hasColorRequirement = paperColorIds.length > 0 || ribbonColorIds.length > 0;

  // Pre-filter rolls to the template's acceptable colors. PAPER rolls are filtered
  // by the paper requirement, RIBBON rolls by the ribbon requirement; a category
  // with no requirement (or "show all") passes everything through.
  const visibleRolls = useMemo(() => {
    if (showAllRolls) return paperRolls;
    return paperRolls.filter((r) => {
      const code = r.item.category?.code;
      const colorId = r.item.color?.id ?? null;
      if (code === "PAPER" && paperColorIds.length) {
        return colorId != null && paperColorIds.includes(colorId);
      }
      if (code === "RIBBON" && ribbonColorIds.length) {
        return colorId != null && ribbonColorIds.includes(colorId);
      }
      return true;
    });
  }, [paperRolls, paperColorIds, ribbonColorIds, showAllRolls]);

  // Keep the selected roll within the visible (filtered) set so a hidden roll is
  // never submitted.
  useEffect(() => {
    setSelectedRollBatchId((prev) => {
      if (prev && visibleRolls.some((r) => r.batchId === prev)) return prev;
      return visibleRolls[0]?.batchId ?? "";
    });
  }, [visibleRolls]);

  const loadTemplates = useCallback(async () => {
    try {
      setTemplates(await api.listBouquetTemplates(locationId || undefined));
    } catch {
      setTemplates([]);
    }
  }, [locationId]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (!templateIdParam) return;
    setTemplateId(templateIdParam);
    void (async () => {
      try {
        const list = await api.listBouquetTemplates();
        const t = list.find((x) => x.id === templateIdParam);
        if (t?.lines?.length) {
          setLines(
            t.lines
              .filter((l) => (l.kind ?? "EXACT_ITEM") === "EXACT_ITEM" && l.itemId)
              .map((l) => ({
              itemId: l.itemId as string,
              quantity: String(l.quantity),
              uom: l.uom,
            })),
          );
        }
        if (t) {
          setShowAllRolls(false);
          setPaperColorIds(colorsForKind(t, "PAPER_REQUIREMENT"));
          setRibbonColorIds(colorsForKind(t, "RIBBON_REQUIREMENT"));
        }
      } catch {
        /* ignore */
      }
    })();
  }, [templateIdParam]);

  const applyTemplate = async () => {
    if (!templateId) return;
    try {
      const full = (await api.listBouquetTemplates()).find((x) => x.id === templateId);
      if (full?.lines?.length) {
        setLines(
          full.lines
            .filter((l) => (l.kind ?? "EXACT_ITEM") === "EXACT_ITEM" && l.itemId)
            .map((l) => ({
            itemId: l.itemId as string,
            quantity: String(l.quantity),
            uom: l.uom,
          })),
        );
      }
      setShowAllRolls(false);
      setPaperColorIds(full ? colorsForKind(full, "PAPER_REQUIREMENT") : []);
      setRibbonColorIds(full ? colorsForKind(full, "RIBBON_REQUIREMENT") : []);
    } catch {
      setError("Could not load template lines");
    }
  };

  const createBouquet = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResultMsg(null);

    try {
      if (includePaper) {
        if (!selectedRollBatchId) {
          throw new Error("Choose a paper or ribbon roll, or uncheck paper.");
        }
        if (paperLength === "CUSTOM") {
          const n = Number(paperCustomCm);
          if (!Number.isFinite(n) || n <= 0) {
            throw new Error("Enter a valid custom length in cm.");
          }
        }
      }

      const body: CreateBouquetCompletePayload = { locationId, zoneId };
      if (templateId) {
        body.templateId = templateId;
      } else {
        const payloadLines = lines.map((l) => {
          const q = Number(l.quantity);
          if (!l.itemId || !Number.isFinite(q) || q <= 0) {
            throw new Error("Lines need item + positive quantity");
          }
          return { itemId: l.itemId, quantity: q, uom: l.uom };
        });
        body.lines = payloadLines;
      }

      if (includePaper && selectedRollBatchId) {
        const roll = paperRolls.find((r) => r.batchId === selectedRollBatchId);
        if (!roll) throw new Error("Selected roll is no longer available. Refresh and try again.");
        if (paperLength === "CUSTOM") {
          body.paperUsages = [
            {
              itemId: roll.item.id,
              batchId: roll.batchId,
              customCm: Number(paperCustomCm),
            },
          ];
        } else {
          body.paperUsages = [
            {
              itemId: roll.item.id,
              batchId: roll.batchId,
              sizePreset: paperLength,
            },
          ];
        }
      }

      const created = await api.createBouquetComplete(body);
      // WO-B (OD-198): the known part of the cost, and how many components have none.
      const unknownNote = unknownComponentsNote(created.unknownCostComponentCount);
      setResultMsg(
        `Bouquet created and stocked (build ${created.id}). ${afterCostLabel("Cost to build", bouquetCostText(created, euro))}${unknownNote ? ` (${unknownNote})` : ""}. Suggested selling price: ${created.suggestedSalePriceEur != null ? euro(Number(created.suggestedSalePriceEur)) : NO_FIGURE}.`,
      );
      setIncludePaper(false);
      setSelectedRollBatchId("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      {embedOnHome ? (
        <div>
          <h3 className="page-title" style={{ margin: 0, fontSize: "1.15rem" }}>
            Build bouquet
          </h3>
          <p className="page-subtitle" style={{ margin: "var(--space-2) 0 0" }}>
            Add flowers, optional paper or ribbon from any in-stock roll, then save once — inventory
            updates in one step.
          </p>
        </div>
      ) : (
        <div>
          <h2 className="page-title">Build bouquet</h2>
          <p className="page-subtitle">
            Add flowers, optional paper or ribbon (any roll with stock), then{" "}
            <strong>Create bouquet</strong>. No need to move rolls between zones first.
          </p>
        </div>
      )}
      {error ? <div className="alert error">{error}</div> : null}
      {resultMsg ? <div className="alert success">{resultMsg}</div> : null}

      <form className="card" onSubmit={createBouquet}>
        <div className="form-grid">
          <div className="field">
            <label>Location *</label>
            <select
              value={locationId}
              onChange={(e) => {
                setLocationId(e.target.value);
                setZoneId("");
              }}
            >
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Zone *</label>
            <select
              value={zoneId}
              onChange={(e) => setZoneId(e.target.value)}
              disabled={!loc}
            >
              {/* G-84: a bouquet is built from the shelves; box stock is
                  reached through the box, which asks who took it. */}
              {shelfZonesOnly(loc?.zones ?? []).map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name} ({z.zoneType})
                </option>
              ))}
            </select>
          </div>
          <div className="field full">
            <label>Template (optional)</label>
            <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "center" }}>
              {templateId ? (() => {
                const sel = templates.find((t) => t.id === templateId);
                if (!sel) return null;
                return (
                  <BouquetTemplateThumb
                    templateId={sel.id}
                    imageKey={sel.imageKey}
                    alt={sel.name}
                    size={56}
                  />
                );
              })() : null}
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">— Manual lines —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <button type="button" className="button tinted" onClick={() => void applyTemplate()}>
                Load lines
              </button>
            </div>
          </div>
        </div>

        {lines.map((line, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "var(--space-3)",
              marginBottom: "var(--space-3)",
              alignItems: "flex-end",
            }}
          >
            <ItemPicker
              label={`Line ${idx + 1}`}
              value={line.itemId}
              onChange={(v) => {
                const next = [...lines];
                next[idx] = { ...next[idx], itemId: v };
                setLines(next);
              }}
            />
            <div className="field">
              <label>Qty</label>
              <input
                type="number"
                min={0.001}
                step="0.001"
                value={line.quantity}
                onChange={(e) => {
                  const next = [...lines];
                  next[idx] = { ...next[idx], quantity: e.target.value };
                  setLines(next);
                }}
              />
            </div>
            <div className="field">
              <label>UoM</label>
              <select
                value={line.uom}
                onChange={(e) => {
                  const next = [...lines];
                  next[idx] = { ...next[idx], uom: e.target.value as DefaultUom };
                  setLines(next);
                }}
              >
                {UOMS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="button plain"
              onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}
              disabled={lines.length <= 1}
            >
              Remove
            </button>
          </div>
        ))}

        <div
          className="field full"
          style={{
            marginTop: "var(--space-3)",
            padding: "var(--space-4)",
            borderRadius: "var(--radius-md)",
            background: "var(--color-surface-muted)",
          }}
        >
          <label style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={includePaper}
              onChange={(e) => setIncludePaper(e.target.checked)}
            />
            <span>Include paper or ribbon</span>
          </label>
          {includePaper ? (
            <div style={{ marginTop: "var(--space-3)", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
              {rollsLoading ? (
                <p className="muted">Loading rolls…</p>
              ) : paperRolls.length === 0 ? (
                <p className="muted" style={{ margin: 0 }}>
                  No CM rolls in stock. Receive paper or ribbon first.
                </p>
              ) : (
                <>
                  {hasColorRequirement ? (
                    <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                      This template prefers specific paper/ribbon colors.{" "}
                      <button
                        type="button"
                        className="button plain"
                        onClick={() => setShowAllRolls((v) => !v)}
                      >
                        {showAllRolls ? "Show matching only" : "Show all rolls"}
                      </button>
                    </p>
                  ) : null}
                  <div className="field">
                    <label htmlFor="bouquet-paper-roll">Roll (any zone)</label>
                    {visibleRolls.length === 0 ? (
                      <p className="muted" style={{ margin: 0 }}>
                        No rolls match the template&apos;s colors.{" "}
                        <button
                          type="button"
                          className="button plain"
                          onClick={() => setShowAllRolls(true)}
                        >
                          Show all rolls
                        </button>
                      </p>
                    ) : (
                      <select
                        id="bouquet-paper-roll"
                        value={selectedRollBatchId}
                        onChange={(e) => setSelectedRollBatchId(e.target.value)}
                      >
                        {visibleRolls.map((r) => (
                          <option key={r.batchId} value={r.batchId}>
                            {r.item.name} — {r.batchCode} ({r.remainingQty} cm) · {r.zoneName}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="field">
                    <span className="muted" style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
                      Length used for this bouquet
                    </span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
                      {(
                        [
                          ["SMALL", "Small — 25 cm"],
                          ["MEDIUM", "Medium — 50 cm"],
                          ["BIG", "Big — 85 cm"],
                        ] as const
                      ).map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          className={paperLength === val ? "button" : "button tinted"}
                          onClick={() => setPaperLength(val)}
                        >
                          {label}
                        </button>
                      ))}
                      <button
                        type="button"
                        className={paperLength === "CUSTOM" ? "button" : "button tinted"}
                        onClick={() => setPaperLength("CUSTOM")}
                      >
                        Custom
                      </button>
                    </div>
                  </div>
                  {paperLength === "CUSTOM" ? (
                    <div className="field" style={{ maxWidth: 200 }}>
                      <label htmlFor="bouquet-paper-custom">Cm</label>
                      <input
                        id="bouquet-paper-custom"
                        type="number"
                        min={0.001}
                        step="0.1"
                        value={paperCustomCm}
                        onChange={(e) => setPaperCustomCm(e.target.value)}
                      />
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
        </div>

        <div className="button-row" style={{ marginTop: "var(--space-3)" }}>
          <button type="button" className="button tinted" onClick={() => setLines((ls) => [...ls, { itemId: "", quantity: "1", uom: "STEM" }])}>
            Add line
          </button>
          <button className="button" type="submit" disabled={busy || !locationId || !zoneId}>
            {busy ? "Working…" : "Create bouquet"}
          </button>
        </div>
      </form>
    </section>
  );
}
